import { Demand2LifecycleStageColumn } from "~/components/demand-2/shared/lifecycleStageColumnId"
import { INSIGHT_KEY_ORDER, type NoosMetricKey } from "~/utils/demandInsightValues"

export enum Demand2TotalColumn {
  Baseline = "demand2-baseline",
  LyRevenue = "demand2-ly-rev",
  PreorderLy = "demand2-preorder-ly",
  Setup = "demand2-setup",
  SignalBased = "demand2-signal-based",
  // Values are persisted in per-user column layouts, so they never change when a header is renamed.
  Adjustments = "demand2-adjustments",
  Forecast = "demand2-forecast",
  ForecastedPreorders = "demand2-forecasted-preorders",
  ConfirmedPreorders = "demand2-confirmed-preorders",
  ReorderConfidence = "demand2-reorder-confidence",
  StockBuy = "demand2-stock-buy",
  TotalDemand = "demand2-total-demand",
  Published = "demand2-published",
  Seasonality = "demand2-seasonality",
  SizeConfidence = "demand2-size-confidence",
  DemandStatus = "demand2-demand-status",
  Lifecycle = "demand2-lifecycle",
  VariantType = "demand2-variant-type",
  Comments = "demand2-comments",
  OwnInsightAnswers = "demand2-own-insight-answers",
  Review = "demand2-review",
}

/**
 * One column per term of the Demand signals column: a model insight by key, or one of the How much
 * card's three sums. Ids are persisted in per-user layouts, so they follow the key, never a label.
 */
export const signalColumnId = (key: NoosMetricKey): string => `demand2-signal-${key}`

export const Demand2SignalColumn = {
  ModelInsights: "demand2-signal-model-insights",
  Strategic: "demand2-signal-strategic",
  Cannibalization: "demand2-signal-cannibalization",
} as const

export const DEMAND2_SIGNAL_COLUMN_IDS: readonly string[] = [
  ...Object.values(Demand2SignalColumn),
  ...INSIGHT_KEY_ORDER.map(signalColumnId),
]

/**
 * The window the engine-computed columns sum over. Not always the season (an undated fashion
 * collection plans the rolling year), so one header can't name it. Module-level: these land in
 * the colDefs memo's deps, and a fresh object each render rebuilds every column.
 */
export const WINDOW_HEADER = {
  headerTooltip:
    "Summed over each variant's own window: the season for fashion, a rolling 52 weeks for the rest.",
}
export const PAST_52W = { headerSubtitle: "Last 52w" }
export const NEXT_52W = { headerSubtitle: "Next 52w" }
export const UNBOOKED_SEASON = { ...WINDOW_HEADER, headerSubtitle: "Not yet booked" }
export const UNBOOKED_52W = { headerSubtitle: "Next 52w · not yet booked" }

/** The Own insight answer column's filter values. The Demand configuration dialog filters on the first. */
export const OWN_ANSWER_VALUE = "Answers on its own"
export const FOLLOWS_COLLECTION_VALUE = "Follows collection"

// Module-level: an inline array would rebuild ProductTableV2's colDefs every render.
export const DEMAND2_TOTAL_DEFAULT_COLUMNS = [
  // A fashion collection is planned as new-vs-carry-over.
  Demand2TotalColumn.VariantType,
  Demand2TotalColumn.Review,
  // The composition waterfall, in fold order.
  Demand2TotalColumn.Baseline,
  Demand2TotalColumn.Setup,
  Demand2TotalColumn.Forecast,
  Demand2TotalColumn.Adjustments,
  Demand2TotalColumn.TotalDemand,
  Demand2TotalColumn.Published,
]

// NOOS has no Type; Lifecycle stage stays in the column chooser.
export const DEMAND2_TOTAL_DEFAULT_COLUMNS_NOOS = [
  Demand2TotalColumn.Review,
  Demand2TotalColumn.Baseline,
  Demand2TotalColumn.Setup,
  Demand2TotalColumn.Forecast,
  Demand2TotalColumn.Adjustments,
  Demand2TotalColumn.TotalDemand,
  Demand2TotalColumn.Published,
]

// Refresh only the demand-driven columns — re-rendering the image/name column re-mounts the <img>.
export const DEMAND2_TOTAL_DATA_COLUMNS = [
  Demand2TotalColumn.Lifecycle,
  Demand2LifecycleStageColumn.LifecycleStage,
  Demand2TotalColumn.VariantType,
  Demand2TotalColumn.Review,
  Demand2TotalColumn.Baseline,
  Demand2TotalColumn.LyRevenue,
  Demand2TotalColumn.PreorderLy,
  Demand2TotalColumn.Seasonality,
  Demand2TotalColumn.SizeConfidence,
  Demand2TotalColumn.Setup,
  ...DEMAND2_SIGNAL_COLUMN_IDS,
  Demand2TotalColumn.SignalBased,
  Demand2TotalColumn.Forecast,
  Demand2TotalColumn.Adjustments,
  Demand2TotalColumn.ForecastedPreorders,
  Demand2TotalColumn.ConfirmedPreorders,
  Demand2TotalColumn.ReorderConfidence,
  Demand2TotalColumn.StockBuy,
  Demand2TotalColumn.TotalDemand,
  Demand2TotalColumn.Published,
  Demand2TotalColumn.DemandStatus,
  Demand2TotalColumn.OwnInsightAnswers,
  // Repaints the count badge when a comment lands, since the counts ride in the grid context.
  Demand2TotalColumn.Comments,
]
